import React, {
  useState,
  memo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import arrayMove from 'array-move';
import { v4 as uuidv4 } from 'uuid';
import actions from '@flux/action';
import { API } from '@service';
import { TreeContainer } from './components';
import ConfigCtx from './services/context.service';
import utils from './services/utils.service';
import alert from '@components/alert';
import PhaseContainer from './components/phase/phase.container';
import modalConfig from './components/modal/modal.config';
import Title from './components/title/title.component';
import ActionButtons from './components/actionButtons/actionButtons.component';
import Expand from './components/expand/expand.component';
// import CommentAnchor from '@components/directUser/CommentAnchor';
// import { shouldScrollInToView } from '@components/directUser/utils';
// import SectionComment from '@components/CommentCenter/SectionComment';
import SectionComment from 'src/ddvComment/SectionComment';

import { useScrollEnd } from '@modules/projects/projectDetail/lifeCycle/services/useScrollEnd';
import useForceUpdate from '@components/useForceUpdate';
import classNames from 'classnames';
import useDragContainerHelper from '@modules/projects/projectDetail/lifeCycle/components/useDragContainerHelper';
import { Button } from 'antd';

export const EMPTY_NODE_ERROR = 'EMPTY_NODE_ERROR';

/*
┌──Module Structure Diagram────────────────────────────────────────────────────────────────────┐
│  ┌────────────────┐              ┌────────────────┐   ┌────────────────┐  ┌────────────────┐ │
│  │     phase      │─────────────▶│                │──▶│                │─▶│                │ │
│  └─┬──────────────┘              └─┬──────────────┘   └─┬──────────────┘  └─┬──────────────┘ │
│    │ ┌────────────┐    ┌ ─ ─ ─ ─ ─ │ ┌────────────┐     │ ┌────────────┐    │ ┌────────────┐ │
│    │ │      ─ ─ ─ ─ ─ ▷   head    ││ │            │     │ │            │    │ │            │ │
│    │ │            │    └ ─ ─ ─ ─ ─ │ │            │     │ │            │    │ │            │ │
│    │ │   node     │     ─ ─ ─ ─ ─  │ │            │     │ │            │    │ │            │ │
│    ├▶│            │    │          │├▶│            │     ├▶│            │    └▶│            │ │
│    │ │      ─ ─ ─ ─ ─ ▷   body    ││ │            │     │ │            │      │            │ │
│    │ └────────────┘    └ ─ ─ ─ ─ ─ │ └────────────┘     │ └────────────┘      └────────────┘ │
│    │                               │                    │                                    │
│    │ ┌────────────┐                │ ┌────────────┐     │ ┌────────────┐                     │
│    │ │            │                │ │            │     │ │            │                     │
│    │ │   node     │                │ │            │     │ │            │                     │
│    └▶│            │                └▶│            │     └▶│            │                     │
│      └────────────┘                  └────────────┘       └────────────┘                     │
└──────────────────────────────────────────────────────────────────────────────────────────────┘
* */
function LifeCycleModule(
  {
    data: {
      treeData: ModuleFormProp = [],
      treeIds = [],
      nodeIds = [],
      idLocations = new Map(),
      idLocationsReset,
      defaultActiveId,
      stepDates: phaseDatesProp,
      defaultSequences: moduleOrderProp,
      section,
    },
    titleName: moduleTitleProp,
    projectId,
    instanceId,
    type,
    typeId,
    identifier,
    projectStatusRefresher,
    isTemplate = false,
    onTitleChange,
    refreshScrollDetection,
    formChange = () => {},
  },
  ref,
) {
  const sectionProp = JSON.stringify(section);
  const [isEditingMode, toggleIsEditingMode] = useState(false);
  const [phaseContents, setPhaseContents] = useState(ModuleFormProp);
  const [phaseDates, setPhaseDates] = useState([]);
  const [idsCollapsed, setIdsCollapsed] = useState(treeIds);
  const [idEditing, setIdEditing] = useState('');
  const [IdActive, setIdActive] = useState({ id: '', isAllActive: false });
  const [moduleOrder, setModuleOrder] = useState(moduleOrderProp);
  const [moduleTitle, setModuleTitle] = useState(moduleTitleProp);
  const forceUpdate = useForceUpdate();

  const moduleFormRef = useRef({ touched: false, data: [] });
  const history = useHistory();
  const isAllNodesExpanding = idsCollapsed.length === 0;

  const dispatch = useDispatch();

  useEffect(() => {
    _resetModule();
    _collapseAllNodes();
    console.log('1', defaultActiveId);
    setIdActive({ ...IdActive, id: defaultActiveId });
  }, [ModuleFormProp, moduleTitleProp]);

  useImperativeHandle(
    ref,
    () => ({
      validate() {
        return {
          lifecycleSectionInfo: {
            sectionTitle: moduleTitle,
            lifecyclePeriods: utils.buildPostBody(
              moduleFormRef.current.data,
              moduleOrder,
              instanceId,
            ),
            sectionDate: moduleFormRef.current.section.date,
            description: moduleFormRef.current.section.description,
          },
        };
      },
      updateTitle(title) {
        setModuleTitle(title);
      },
    }),
    [moduleTitle, moduleOrder, instanceId],
  );

  const _resetModule = () => {
    setPhaseContents(ModuleFormProp);
    setPhaseDates(phaseDatesProp);
    setModuleTitle(moduleTitleProp);
    setModuleOrder(moduleOrderProp);
    setIdEditing('');
    toggleIsEditingMode(false);
    moduleFormRef.current = {
      touched: false,
      data: ModuleFormProp,
      section: JSON.parse(sectionProp),
    };
    if (isTemplate) {
      setTimeout(() => {
        toggleIsEditingMode(true);
      }, 0);
    }
  };

  const _collapseAllNodes = () => {
    setIdsCollapsed(treeIds);
  };

  const _resetIdLocations = () => {
    idLocations.clear();
    [...idLocationsReset.entries()].forEach((e) => {
      idLocations.set(e[0], e[1]);
    });
  };

  const updateModuleFormRefBeginEndDate = function (event, type) {
    // Each module has either begin or end date
    switch (type) {
      case 'date':
        moduleFormRef.current.section.date = event?.format();
        break;
      case 'description':
        moduleFormRef.current.section.description = event.target.value;
        break;
      default:
        break;
    }
    moduleFormRef.current.touched = true;
    forceUpdate();
    formChange();
  };

  const updateModuleFormRef = function (data = {}, isCompleteContent, isAdd) {
    if (isCompleteContent) {
      // case 1:  replace the ref's value to data directly
      if (data) {
        moduleFormRef.current.data = data;
      }
      moduleFormRef.current.touched = true;
      return;
    }
    // case 2: modify the ref's value by location [x,y] and assign it back to ref
    const updatedModuleForm = utils.buildUpdatedModuleForm(
      moduleFormRef.current.data,
      data,
      isAdd,
    );
    if (updatedModuleForm) {
      moduleFormRef.current.data = updatedModuleForm.data;
      moduleFormRef.current.touched = updatedModuleForm.touched;
    }
  };
  const updateIdActive = (id) => {
    if (!IdActive.isAllActive && IdActive.id === id) {
      return;
    }
    console.log('2', id);
    setIdActive({ id, isAllActive: false });
  };

  const updateIdActiveAndIdsCollapsed = (newId) => {
    if (isEditingMode) {
      return;
    }
    let newActiveId = '',
      currentActiveId = IdActive.id;

    setIdsCollapsed((collapsedIds) => {
      if (collapsedIds.includes(newId)) {
        newActiveId = newId;
        console.log('3', newActiveId);
        setIdActive({ id: newActiveId, isAllActive: !newActiveId });
        return collapsedIds.filter((a) => a !== newId);
      }
      newActiveId = newId !== currentActiveId ? currentActiveId : '';
      console.log('4', newActiveId);
      setIdActive({ id: newActiveId, isAllActive: !newActiveId });
      return [...collapsedIds, newId];
    });
  };

  const handleCancel = () => {
    _resetModule();
    _resetIdLocations();
  };

  const collapseAll = (e) => {
    setIdsCollapsed(treeIds);

    console.log('5, 清空');
    setIdActive({ id: '', isAllActive: false });
    _resetModule();
  };

  const expandAll = () => {
    setIdsCollapsed([]);

    console.log('6, 清空');
    setIdActive({ id: '', isAllActive: true });
  };

  const expandPhaseLevel = () => {
    setIdsCollapsed([...nodeIds]);

    console.log('7, 清空');
    setIdActive({ id: '', isAllActive: false });
  };

  const handleSave = async ({ afterSave }) => {
    if (!moduleFormRef.current.touched && moduleTitle === moduleTitleProp) {
      handleCancel();
      setTimeout(() => {
        afterSave && afterSave();
      }, 0);
      return;
    }
    try {
      const result = await dispatch(
        actions.submitLifecycleData(
          {
            lifecycleSectionInfo: {
              sectionTitle: moduleTitle,
              lifecyclePeriods: utils.buildPostBody(
                moduleFormRef.current.data,
                moduleOrder,
              ),
              sectionDate: moduleFormRef.current.section.date,
              description: moduleFormRef.current.section.description,
            },
          },
          typeId,
        ),
      );
      if (result.success) {
        setIdEditing('');
        toggleIsEditingMode(false);
        projectStatusRefresher();
      }
      afterSave && afterSave();
    } catch (error) {
      if (error.message === EMPTY_NODE_ERROR) {
        alert.warning(modalConfig.emptyAlert);
      }
    }
  };

  const handleAddNewPhase = () => {
    formChange();
    const newPhase = {
      id: uuidv4(),
      order: phaseContents.length,
    };
    newPhase.content = [
      ...moduleFormRef.current.data,
      utils.createNewPhaseWithTemplate(
        newPhase.order,
        newPhase.id,
        type,
        phaseContents,
      ),
    ];
    setPhaseDates((prev) => [...prev, null]);
    // update moduleFormRef in ref
    updateModuleFormRef(newPhase.content, true);
    // update phaseContents in state
    setPhaseContents(newPhase.content);
    // update idLocations in variables(coming from props)
    idLocations.set(newPhase.id, [newPhase.order]);
    // update moduleOrder in state
    setModuleOrder({
      ...moduleOrder,
      [newPhase.id]: [],
    });
    // scroll to the right most when add new phase
    setTimeout(() => {
      handleScroll(2000);
    }, 100);
  };

  const handleAddNode = (phaseId) => {
    formChange();
    if (!phaseId) {
      return;
    }

    const newNode = {
      order: utils.getNodesByPhaseId(moduleFormRef, phaseId).length + 1,
    };
    newNode.location = [...idLocations.get(phaseId), newNode.order - 1];

    // update moduleFormRef in ref
    updateModuleFormRef(
      {
        location: newNode.location,
        payload: { order: newNode.order },
      },
      false,
      true,
    );

    // update phaseContents in state
    setPhaseContents(moduleFormRef.current.data);

    // update idLocations in variables(coming from props)
    const phaseItemsById = moduleFormRef.current.data.find(
      (e) => e.id === phaseId,
    ).lifecycleItems;
    const newItemId = phaseItemsById[phaseItemsById.length - 1].id;
    idLocations.set(newItemId, newNode.location);

    // update moduleOrder in state
    setModuleOrder({
      ...moduleOrder,
      [phaseId]: [...moduleOrder[phaseId], newNode.order - 1],
    });
  };

  const handleDeletePhase = (containerId) => {
    formChange();
    const deletedIndex = phaseContents.findIndex((e) => e.id === containerId);
    // update moduleFormRef in ref
    updateModuleFormRef(
      moduleFormRef.current.data.filter((e) => e.id !== containerId),
      true,
    );
    setPhaseDates((prev) => prev.filter((e, i) => i !== deletedIndex));
    // update phaseContents in state
    setPhaseContents(phaseContents.filter((e) => e.id !== containerId));
    // update idLocations in variables(coming from props)
    const idLocationsInObjectArrayForm = [...idLocations.entries()];
    //     know the deleted phase's location info [x]
    const deletedXAxis = idLocations.get(containerId)[0];
    //     any node (phase and node) that is more than x needs to be modified by -1
    for (let i = 0; i < idLocationsInObjectArrayForm.length; i++) {
      const [id, [x, y]] = idLocationsInObjectArrayForm[i];
      if (x > deletedXAxis) {
        if (y !== undefined) {
          idLocations.set(id, [x - 1, y]);
        } else {
          idLocations.set(id, [x - 1]);
        }
      }
      //   delete the phase location, and it's associated nodes location from idLocations
      if (x === deletedXAxis) {
        idLocations.delete(id);
      }
    }
    // update moduleOrder in state
    setModuleOrder((orderInState) =>
      utils.removeObjectProperty(orderInState, containerId),
    );
  };

  const handleDeleteNode = (containerId, deleteIndex, nodeId) => {
    formChange();
    moduleFormRef.current.touched = true;
    // update moduleOrder in state
    setModuleOrder((orderInState) => {
      const sequences = orderInState;
      const targetSequence = sequences[containerId];
      return {
        ...sequences,
        [containerId]: targetSequence.filter((item) => item !== deleteIndex),
      };
    });
  };

  const handleMovePhaseEnd = (oldPhaseIndex, newPhaseIndex) => {
    console.log('move phase', oldPhaseIndex, newPhaseIndex);
    formChange();
    const oldPhase = moduleFormRef.current.data[oldPhaseIndex];
    const newPhase = moduleFormRef.current.data[newPhaseIndex];

    const oldPhaseId = oldPhase.id;
    const newPhaseId = newPhase.id;

    const oldNodesIds = oldPhase.lifecycleItems.map((e) => e.id);
    const newNodesIds = newPhase.lifecycleItems.map((e) => e.id);
    // needed to prevent node body lost after drag

    console.log('8', oldPhaseId);
    setIdActive({ ...IdActive, id: oldPhaseId });
    // // update phaseContents and stepDate in state
    const newPhaseContents = arrayMove(
      phaseContents,
      oldPhaseIndex,
      newPhaseIndex,
    );
    const newModuleRefData = arrayMove(
      moduleFormRef.current.data,
      oldPhaseIndex,
      newPhaseIndex,
    );

    const newPhaseDates = [...phaseDates];
    newModuleRefData[oldPhaseIndex].startAt = null;
    newModuleRefData[newPhaseIndex].startAt = null;
    newPhaseContents[oldPhaseIndex].startAt = null;
    newPhaseContents[newPhaseIndex].startAt = null;
    newPhaseDates[oldPhaseIndex] = null;
    newPhaseDates[newPhaseIndex] = null;

    moduleFormRef.current = {
      ...moduleFormRef.current,
      touched: true,
      data: newModuleRefData,
    };
    setPhaseDates(newPhaseDates);
    setPhaseContents(newPhaseContents);

    // update idLocations in variables(coming from props)
    //   update phase level
    idLocations.set(oldPhaseId, [newPhaseIndex]);
    idLocations.set(newPhaseId, [oldPhaseIndex]);
    //   update item level
    for (let i = 0; i < oldNodesIds.length; i++) {
      const oldItemId = oldNodesIds[i];
      idLocations.set(oldItemId, [
        newPhaseIndex,
        idLocations.get(oldItemId)[1],
      ]);
    }
    for (let i = 0; i < newNodesIds.length; i++) {
      const newItemId = newNodesIds[i];
      idLocations.set(newItemId, [
        oldPhaseIndex,
        idLocations.get(newItemId)[1],
      ]);
    }
  };

  const handleMoveNodeEnd = ({ collection, newIndex, oldIndex }) => {
    console.log('move node');
    formChange();
    const sequence = moduleOrder[collection] || [];

    updateModuleFormRef(null, true);
    setModuleOrder({
      ...moduleOrder,
      [collection]: arrayMove(sequence, oldIndex, newIndex),
    });
  };

  const handleShowDeletePhaseConfirmModal = (...args) => {
    if (moduleFormRef.current.data.length === 1) {
      alert.warning(modalConfig.lastPhaseAlert);
      return;
    }
    alert.confirmWithSave({
      onOk() {
        handleDeletePhase(...args);
      },
      ...modalConfig.deletePhaseConfirm,
    });
  };
  const handleShowDeleteNodeConfirmModal = (...args) => {
    if (moduleOrder[args[0]].length === 1) {
      alert.warning(modalConfig.lastNodeAlert);
      return;
    }
    alert.confirmWithSave({
      onOk() {
        handleDeleteNode(...args);
      },
      ...modalConfig.deleteNodeConfirm,
    });
  };
  const handlePrompt = (location) => {
    if (isEditingMode || idEditing) {
      alert.confirmWithSave({
        onOk() {
          handleSave({ afterSave: () => history.push(location.pathname) });
        },
        onCancel(close) {
          handleCancel();
          close();
          setTimeout(() => {
            history.push(location.pathname);
          }, 0);
        },
        ...modalConfig.leaveConfirm,
      });
      return false;
    }
    return true;
  };

  function handleScroll(amount) {
    document.getElementsByClassName(`content_${type}`)[0].scrollLeft += amount;
  }

  const configCtx = {
    changeTreeDataCollection: updateModuleFormRef,
    updateModuleFormRefBeginEndDate,
    section,
    changeCollapsedIds: updateIdActiveAndIdsCollapsed,
    collapsedIds: idsCollapsed,
    idLocations,
    isAllExpanding: isAllNodesExpanding || isEditingMode,
    isAllEditing: isEditingMode,
    editingId: idEditing,
    setEditingId: setIdEditing,
    activeId: IdActive.id,
    isAllActive: IdActive.isAllActive,
    setActiveId: updateIdActive,
    defaultActiveId,
    handleSortEnd: handleMoveNodeEnd,
    handleDeleteNode: handleShowDeleteNodeConfirmModal,
    handleDeletePhase: handleShowDeletePhaseConfirmModal,
    handleMovePhaseEnd,
  };

  const containerRef = useRef();
  const scrollEnd = useScrollEnd(
    containerRef,
    isEditingMode + refreshScrollDetection,
  );

  const className = classNames('integration-navigator', 'life-cycle', {
    'no-scroll': phaseContents.length <= 4,
  });

  useDragContainerHelper();

  return (
    <div className={className}>
      {!isTemplate ? <Prompt message={handlePrompt} /> : null}
      <div className="title">
        <Title
          isEditing={isEditingMode}
          content={moduleTitle}
          setContent={(newVal) => {
            formChange();
            setModuleTitle(newVal);
          }}
        />
        {!isTemplate ? (
          <div className="button-with-comment-icon">
            <SectionComment
              module="Project Home"
              component={moduleTitle}
              placement="leftTop"
              sendIdentifier={identifier}
              getPopupContainer={() =>
                document.getElementsByClassName('project-detail')[0] || null
              }
            />
            <ActionButtons
              isEditing={isEditingMode || idEditing}
              handleCancel={handleCancel}
              handleEditingAll={() => toggleIsEditingMode(true)}
              handleSave={handleSave}
              handleAddNewPhase={handleAddNewPhase}
            />
          </div>
        ) : (
          <div className="action-wrapper role-control">
            <Button
              className="add-btn dd-cancel-btn"
              onClick={handleAddNewPhase}
              icon={<span className="appkiticon icon-plus-fill" />}
            >
              Add Section
            </Button>
          </div>
        )}
      </div>
      {isEditingMode ? null : (
        <Expand
          collapseAll={collapseAll}
          expandAll={expandAll}
          expandPhase={expandPhaseLevel}
          // isAllExpand={isAllNodesExpanding || isEditingMode}
        />
      )}
      <div className="lifecycle-navButton-container">
        {!scrollEnd.isLeftEnd && (
          <button
            className="lifecycle-navButton appkiticon icon-left-chevron-fill"
            onClick={() => handleScroll(-310)}
          />
        )}
        <div /> {/* needed for placement when left error don't render*/}
        {!scrollEnd.isRightEnd && (
          <button
            className="lifecycle-navButton appkiticon icon-right-chevron-fill"
            onClick={() => handleScroll(310)}
          />
        )}
      </div>

      <ConfigCtx.Provider value={configCtx}>
        <div className={`content_${type}`} ref={containerRef}>
          <PhaseContainer>
            {phaseContents?.map((phase, index) => (
              <TreeContainer
                id={phase.id}
                key={phase.id}
                title={
                  isEditingMode
                    ? moduleFormRef.current?.data?.[index]?.title
                    : phase.title
                }
                order={index}
                type={type}
                isFirst={index === 0}
                isLast={index === phaseContents.length - 1}
                stepDes={utils.buildPhaseDateDetails(
                  phase,
                  index,
                  phaseDates,
                  setPhaseDates,
                  type,
                  moduleFormRef?.current?.section?.date,
                  moduleFormRef.current?.data?.[index]?.name,
                )}
                nodes={utils.getNodes(moduleFormRef, moduleOrder, phase.id)}
                changeTreeDataCollection={updateModuleFormRef}
                handleAddNode={handleAddNode}
                formChange={formChange}
              />
            ))}
          </PhaseContainer>
        </div>
      </ConfigCtx.Provider>
    </div>
  );
}

LifeCycleModule.propTypes = {};

export default forwardRef(LifeCycleModule);
